import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchSocialMedia,
  createSocialMedia,
  updateSocialMedia,
  deleteSocialMedia,
} from "../../api/controller/admin/adminSocialMedia";
import AdminSocialMediaDialog from "./adminDialogBoxes/AdminSocialMediaDialog";
import AdminConfirmDialog from "./adminDialogBoxes/AdminConfirmDialog";
import { useFlashMessage } from "../../components/FlashMessageProvider";

const AdminSocialMedia = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const { showMessage } = useFlashMessage();
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchSocialMedia();
        if (mounted) setItems(res || []);
      } catch (err) {
        console.error(err);
        if (mounted) setError("Failed to load social media items.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  const userType = localStorage.getItem("userType");
  const isAdmin = userType === "admin";

  if (!isAdmin) {
    return (
      <div className="p-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
        <p>You do not have permission to view this page.</p>
        <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => navigate("/")}>Go Home</button>
      </div>
    );
  }

  const refresh = async () => {
    try {
      const res = await fetchSocialMedia();
      setItems(res || []);
    } catch (err) {
      console.error(err);
      setError("Failed to refresh social media items.");
    }
  };

  const handleAdd = () => {
    setDialogInitial(null);
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setDialogInitial(item);
    setDialogOpen(true);
  };

  const handleSave = async (formData) => {
    setIsSaving(true);
    try {
      const isEditing = Boolean(dialogInitial && (dialogInitial._id || dialogInitial.id));
      if (isEditing) {
        await updateSocialMedia(dialogInitial._id || dialogInitial.id, formData);
      } else {
        await createSocialMedia(formData);
      }
      await refresh();
      showMessage(isEditing ? 'Social media item updated.' : 'Social media item added.', { type: 'success' });
      setDialogOpen(false);
      setDialogInitial(null);
    } catch (err) {
      console.error(err);
      showMessage('Failed to save social media item.', { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteRequest = (item) => {
    setDeleteTarget({ id: item._id || item.id, name: item.platform });
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setIsDeleting(true);
    try {
      await deleteSocialMedia(deleteTarget.id);
      await refresh();
      showMessage('Social media item deleted.', { type: 'success' });
      setIsDeleteDialogOpen(false);
      setDeleteTarget(null);
    } catch (err) {
      console.error(err);
      showMessage('Failed to delete social media item.', { type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (isDeleting) return;
    setIsDeleteDialogOpen(false);
    setDeleteTarget(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-4">Admin — Social Media</h1>

      <div className="mb-4">
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded">Add Social Media</button>
      </div>

      {loading ? (
        <p className="text-gray-600">Loading social media...</p>
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <div className="space-y-4">
          {items.map((item) => (
            <div key={item._id || item.id} className="bg-white p-4 rounded shadow flex items-start gap-4">
              <div className="flex-1">
                <h3 className="text-xl font-semibold">{item.platform} {item.handle ? `(${item.handle})` : ''}</h3>
                <p className="text-sm text-gray-600">{item.url}</p>
                <p className="mt-2 text-gray-700">{item.description}</p>
                <p className="mt-2 text-gray-700">Followers: {item.followers}</p>
                <p className="mt-2 text-gray-700">Active: {item.active ? 'Yes' : 'No'}</p>
                <div className="mt-3">
                  <button onClick={() => handleEdit(item)} className="text-blue-600 mr-4">Edit</button>
                  <button onClick={() => handleDeleteRequest(item)} className="text-red-600">Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AdminSocialMediaDialog
        open={dialogOpen}
        initial={dialogInitial}
        onClose={() => {
          if (isSaving) return;
          setDialogOpen(false);
          setDialogInitial(null);
        }}
        onSave={handleSave}
        isSaving={isSaving}
      />
      <AdminConfirmDialog
        open={isDeleteDialogOpen}
        title="Delete Social Media"
        message={deleteTarget?.name ? `Are you sure you want to delete the ${deleteTarget.name} entry? This action cannot be undone.` : 'Are you sure you want to delete this social media item?'}
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default AdminSocialMedia;
